import type { ContextVisibility, DisplayVisibility, EditPermission, JsonObject, JsonValue, WorldDocumentKind } from "./types";

export type FieldType = "text" | "long_text" | "number" | "boolean" | "enum" | "image" | "reference" | "list" | "object";

export interface FieldDefinition {
  schema?: import("./structured-schema").StructuredSchema;
  id: string;
  label: string;
  type: FieldType;
  required?: boolean;
  defaultValue?: JsonValue;
  options?: string[];
  referenceKinds?: WorldDocumentKind[];
  item?: FieldDefinition;
  properties?: FieldDefinition[];
  contextPriority?: "always" | "active" | "retrieved" | "never";
  contextVisibility?: ContextVisibility;
  visibleTo?: DisplayVisibility[];
  editableBy?: EditPermission[];
}

export type LayoutComponentType =
  | "stack"
  | "grid"
  | "card"
  | "tabs"
  | "accordion"
  | "collapsible"
  | "separator"
  | "image_header"
  | "image"
  | "avatar"
  | "heading"
  | "text"
  | "markdown"
  | "callout"
  | "alert"
  | "badge"
  | "badge_list"
  | "list"
  | "stat_grid"
  | "derived_stat_grid"
  | "metadata_grid"
  | "field_list"
  | "table"
  | "data_table"
  | "progress"
  | "dots"
  | "stepper"
  | "toggle"
  | "checkbox"
  | "track_boxes"
  | "condition_toggles"
  | "clock"
  | "checklist"
  | "condition_chips"
  | "select"
  | "voice_select"
  | "formatted_date"
  | "formatted_number"
  | "reference_grid"
  | "relationship_list"
  | "reference_value"
  | "include"
  | "section_switch"
  | "map_preview"
  | "turn_tracker";

export interface LayoutResponsiveValue {
  /** Number of columns a container exposes at this viewport. */
  columns?: number;
  /** Number of parent grid columns occupied by this component. */
  span?: number;
  hidden?: boolean;
  order?: number;
}

export interface LayoutResponsiveProps {
  phone?: LayoutResponsiveValue;
  tablet?: LayoutResponsiveValue;
  desktop?: LayoutResponsiveValue;
}

export interface VisibilityCondition {
  field: string;
  operator: "exists" | "equals" | "not_equals" | "greater_than";
  value?: JsonValue;
}

export interface LayoutComponent {
  id: string;
  type: LayoutComponentType;
  props?: JsonObject;
  title?: string;
  binding?: string;
  columns?: number;
  children?: LayoutComponent[];
  repeatFrom?: string;
  visibleWhen?: VisibilityCondition[];
  styleToken?: string;
  responsive?: LayoutResponsiveProps;
}

export type FileViewMode = "full" | "compact" | "mention";

export interface EntityTemplate {
  id: string;
  kind: WorldDocumentKind;
  version: number;
  name: string;
  fields: FieldDefinition[];
  layout: LayoutComponent[];
  imagePolicy: {
    autoGenerateCover: boolean;
    maxAutoGeneratedImages: 1;
    maxUserImages: number;
    stylePreset: "story" | "realistic" | "anime" | "cartoon" | "2.5d";
  };
}

export interface HeaderSlot {
  id: string;
  label: string;
  emoji?: string;
  colorToken: string;
  sourceBinding: string;
  priority: number;
  showWhen?: VisibilityCondition[];
  fallback?: string;
}

export interface SceneHeaderTemplate {
  id: string;
  version: number;
  themeToken: string;
  layout: "compact" | "cinematic" | "dashboard";
  maxVisibleSlots: number;
  slots: HeaderSlot[];
  calendarFormat: string;
  locationBinding: string;
}

export interface CreationFlowStep {
  id: string;
  title: string;
  description?: string;
  kind: "text" | "long_text" | "choice" | "reference" | "image" | "review";
  resultBinding: string;
  options?: { id: string; label: string; description?: string; image?: string }[];
  required?: boolean;
  visibleWhen?: VisibilityCondition[];
}

export interface CharacterCreationFlow {
  id: string;
  version: number;
  title: string;
  steps: CreationFlowStep[];
}

export function normalizeComponentType(type: string | undefined): LayoutComponentType {
  if (!type) return "stack";
  const map: Record<string, LayoutComponentType> = {
    ImageHeader: "image_header",
    image_header: "image_header",
    Stack: "stack",
    stack: "stack",
    Grid: "grid",
    grid: "grid",
    Card: "card",
    card: "card",
    Heading: "heading",
    heading: "heading",
    Text: "text",
    text: "text",
    Badge: "badge",
    badge: "badge",
    BadgeList: "badge_list",
    badge_list: "badge_list",
    ConditionChips: "condition_chips",
    condition_chips: "condition_chips",
    Progress: "progress",
    progress: "progress",
    Tabs: "tabs",
    tabs: "tabs",
    Checkbox: "checkbox",
    checkbox: "checkbox",
    Switch: "toggle",
    Toggle: "toggle",
    toggle: "toggle",
    List: "list",
    list: "list",
    Callout: "callout",
    callout: "callout",
    Alert: "alert",
    alert: "alert",
    ReferenceValue: "reference_value",
    reference_value: "reference_value",
    Markdown: "markdown",
    markdown: "markdown",
    StatGrid: "stat_grid",
    stat_grid: "stat_grid",
    DerivedStatGrid: "derived_stat_grid",
    derived_stat_grid: "derived_stat_grid",
    MetadataGrid: "metadata_grid",
    metadata_grid: "metadata_grid",
    Checklist: "checklist",
    checklist: "checklist",
    Include: "include",
    include: "include",
    SectionSwitch: "section_switch",
    section_switch: "section_switch",
    Dots: "dots",
    dots: "dots",
    Stepper: "stepper",
    stepper: "stepper",
    TrackBoxes: "track_boxes",
    track_boxes: "track_boxes",
    ConditionToggles: "condition_toggles",
    condition_toggles: "condition_toggles",
    Clock: "clock",
    clock: "clock",
    DataTable: "data_table",
    data_table: "data_table",
    Table: "table",
    table: "table",
    FieldList: "field_list",
    field_list: "field_list",
    FormattedDate: "formatted_date",
    formatted_date: "formatted_date",
    FormattedNumber: "formatted_number",
    formatted_number: "formatted_number",
    Select: "select",
    select: "select",
    VoiceSelect: "voice_select",
    voice_select: "voice_select",
    FileReferenceGrid: "reference_grid",
    PageReferenceGrid: "reference_grid",
    ReferenceGrid: "reference_grid",
    reference_grid: "reference_grid",
    RelationshipList: "relationship_list",
    relationship_list: "relationship_list",
    TurnTracker: "turn_tracker",
    turn_tracker: "turn_tracker",
    Separator: "separator",
    separator: "separator",
    Accordion: "accordion",
    accordion: "accordion",
    Collapsible: "collapsible",
    collapsible: "collapsible",
    Avatar: "avatar",
    avatar: "avatar",
    Image: "image",
    image: "image",
    MapPreview: "map_preview",
    map_preview: "map_preview",
  };
  return map[type] || (type.toLowerCase().replace(/([a-z])([A-Z])/g, "$1_$2").toLowerCase() as LayoutComponentType);
}
